import { useLingui } from "@lingui/react/macro";
import { useTenantList } from "@/hooks/useTenantLists";

/**
 * How a prepped item is held.
 *
 * Shared by Shelf Life & Storage and by Labeling (T-1002): the two panels edit
 * the same field on the same row, so they must offer the same list. T-1004
 * settled the list on these five and canonicalised the older spellings in the
 * database; the stored value is still the English string.
 *
 * Since T-1006 the list belongs to the tenant, and these five are the seed the
 * API hands a kitchen that has never opened Configuration. They stay here as
 * well so a picker still has options while that request is in flight.
 */
export const STORAGE_METHODS = [
  "Refrigerated",
  "Frozen",
  "Ambient",
  "Hot-held",
  "RTE",
] as const;

/** Spellings that predate T-1004. The migration rewrites them, but a row that
 * escaped it must still show what it holds rather than appear blank. */
const LEGACY_NAMES: Record<string, string> = {
  Chilled: "Chilled",
  "Hot Hold": "Hot Hold",
  "Hot hold": "Hot hold",
  "Room Temperature": "Room Temperature",
};

/**
 * The options to offer, given what is stored.
 *
 * A value the list does not contain is appended rather than dropped: a select
 * that cannot represent the current value silently rewrites it on the next
 * save, which is how a frozen stage becomes a refrigerated one.
 */
export function storageMethodOptions(
  current?: string | null,
  catalog?: readonly string[],
): string[] {
  const options: string[] = [...(catalog ?? STORAGE_METHODS)];
  if (current && !options.includes(current)) options.push(current);
  return options;
}

/** The tenant's storage methods, falling back to the seed while they load. */
export function useStorageMethods(): readonly string[] {
  return useTenantList("storageMethods") ?? STORAGE_METHODS;
}

export function useStorageMethodNames(): Record<string, string> {
  const { t } = useLingui();
  return {
    Refrigerated: t`Refrigerated`,
    Frozen: t`Frozen`,
    Ambient: t`Ambient`,
    "Hot-held": t`Hot-held`,
    RTE: t`Ready to eat`,
    ...LEGACY_NAMES,
  };
}
